import * as React from "react";
import { homeLink, request } from "../../../../../services/cpln";
import { Button, Card, Space, Tooltip } from "antd";
import { CardTitle } from "../../../../../components/antd/card/CardTitle";
import { CardContent } from "../../../../../components/antd/card/CardContent";
import { Mk8sInput } from "../../../../../schema/types/mk8s/input/mk8s";
import { CardCollapsibleContent } from "../../../../../components/antd/card/CardCollapsibleContent";
import { CaretRightOutlined, EllipsisOutlined, PlusOutlined } from "@ant-design/icons";
import { v4 as uuidv4 } from "uuid";
import { NGProviderBrowser } from "../../../ngProviderBrowser";
import { inputToMk8s } from "../../../draft/utils/inputToMk8s";
import { ProviderPreInstallScript } from "../../../advanced/providerPreInstallScript";
import { ProviderAutoscaler } from "../../../advanced/providerAutoscaler";
import { ProviderNetworking } from "../../../advanced/providerNetworking";
import { formSetValue, formTriggerValidation, useCpFormValue } from "../../../../gvc/testNewForm/utils";
import { CpInput } from "../../../../gvc/testNewForm/CpInput";
import { CpFormItem } from "../../../../gvc/testNewForm/CpFormItem";
import { CpLabel } from "../../../../gvc/testNewForm/CpLabel";
import { CpErrorWarning } from "../../../../gvc/testNewForm/CpErrorWarning";
import { CpArrayString } from "../../../../gvc/testNewForm/CpArrayString";
import { store } from "../../../../../store";
import { cloneDeep } from "lodash";
import { useCpFormContext } from "../../../../../components/form/useCpFormContext";
import { CpErrorWarningArrayIcon } from "../../../../gvc/testNewForm/CpErrorWarningArrayIcon";
import { CpKindSelect } from "../../../../gvc/testNewForm/temp/CpKindSelect";
import { useDrawerContext } from "../../../../../components/antd/drawerContext";
import { EmbeddedSecretCreate } from "../../../../secret/create/embedded";
import { parseLink } from "../../../../../utils/linkParser/linkParser";
import { CpLabelTooltip } from "../../../../gvc/testNewForm/CpLabelTooltip";
import { CpTooltip } from "../../../../gvc/testNewForm/CpTooltip";

interface Props {}
export const LinodeProviderSelf: React.FC<Props> = ({}) => {
  const { id } = useCpFormContext();

  const drawer = useDrawerContext();

  const [browserKey, setBrowserKey] = React.useState("");
  const [isAdvancedCollapsed, setIsAdvancedCollapsed] = React.useState(true);

  const region = useCpFormValue(`linode.region`);
  const firewallId = useCpFormValue(`linode.firewallId`);
  const vpcId = useCpFormValue(`linode.vpcId`);
  const image = useCpFormValue(`linode.image`);
  const authorizedUsers = useCpFormValue(`linode.authorizedUsers`);

  return (
    <>
      <div className="cpln-form-section cpln-ant-card-container">
        <Card>
          <CardTitle>
            Linode Provider
            <CpErrorWarningArrayIcon
              names={[
                `linode.region`,
                `linode.tokenSecretLink`,
                `linode.firewallId`,
                `linode.image`,
                `linode.authorizedUsers`,
                `linode.authorizedKeys`,
                `linode.vpcId`,
                `linode.networking`,
                `linode.preInstallScript`,
                `linode.autoscaler`,
              ]}
            />
          </CardTitle>
          <CardContent>
            <CpFormItem>
              <CpLabel name={`linode.region`}>Region</CpLabel>
              <Space.Compact>
                <CpInput name={`linode.region`} style={{ width: 600 }} />
                <Tooltip title={`Browse`}>
                  <Button icon={<EllipsisOutlined />} onClick={() => setBrowserKey("regions")} />
                </Tooltip>
              </Space.Compact>
              <CpErrorWarning name={`linode.region`} />
            </CpFormItem>
            <CpFormItem>
              <CpLabelTooltip>
                <CpLabel name={`linode.tokenSecretLink`}>Linode Token Secret</CpLabel>
                <CpTooltip
                  tooltipProps={{
                    title: `An opaque secret that contains your Linode token, it will be used to manage servers in your Linode account`,
                  }}
                />
              </CpLabelTooltip>
              <Space.Compact>
                <CpKindSelect
                  kind="secret"
                  name={`linode.tokenSecretLink`}
                  optionValue="name"
                  queries={[{ property: "type", value: "opaque" }]}
                  style={{ width: 600 }}
                />
                <Tooltip title={`Create an Opaque Secret`}>
                  <Button
                    onClick={() => {
                      drawer.setPersistent(
                        `mk8s-linode-create-secret`,
                        "Create Opaque Secret",
                        <>
                          <EmbeddedSecretCreate
                            onCancel={() => drawer.closePersistent()}
                            onDone={(itemLink) => {
                              drawer.closePersistent();
                              const { name } = parseLink(itemLink, { useInputCtx: true });
                              const secretState = {
                                value: name,
                                name: name,
                                link: itemLink,
                              };
                              formSetValue(id, `linode.tokenSecretLink`, secretState);
                              formTriggerValidation(id);
                            }}
                            lockedType={`opaque`}
                          />
                        </>,
                        600,
                      );
                    }}
                    icon={<PlusOutlined />}
                  />
                </Tooltip>
              </Space.Compact>
              <CpErrorWarning name={`linode.tokenSecretLink`} />
            </CpFormItem>
            <CpFormItem>
              <CpLabel name={`linode.image`}>Image</CpLabel>
              <Space.Compact>
                <CpInput name={`linode.image`} style={{ width: 600 }} />
                <Tooltip title={`Browse`}>
                  <Button icon={<EllipsisOutlined />} onClick={() => setBrowserKey("images")} />
                </Tooltip>
              </Space.Compact>
              <CpErrorWarning name={`linode.image`} />
            </CpFormItem>
            <CpFormItem>
              <CpLabel name={`linode.vpcId`}>VPC ID</CpLabel>
              <Space.Compact>
                <CpInput name={`linode.vpcId`} style={{ width: 600 }} />
                <Tooltip title={`Browse`}>
                  <Button icon={<EllipsisOutlined />} onClick={() => setBrowserKey("vpcs")} />
                </Tooltip>
              </Space.Compact>
              <CpErrorWarning name={`linode.vpcId`} />
            </CpFormItem>
            <CpFormItem>
              <CpLabel name={`linode.firewallId`}>Firewall ID</CpLabel>
              <Space.Compact>
                <CpInput name={`linode.firewallId`} style={{ width: 600 }} />
                <Tooltip title={`Browse`}>
                  <Button icon={<EllipsisOutlined />} onClick={() => setBrowserKey("firewalls")} />
                </Tooltip>
              </Space.Compact>
              <CpErrorWarning name={`linode.firewallId`} />
            </CpFormItem>
            <div style={{ maxWidth: 600 }}>
              <CpArrayString name={`linode.authorizedUsers`} label={`Authorized Users`} />
            </div>
            <div className="mt-4">
              <CardTitle style={{ marginBottom: 0, fontSize: 20 }}>
                <div className={`flex items-baseline ${isAdvancedCollapsed ? "" : "mb-4"}`}>
                  <div
                    className={`flex items-baseline gap-2 color-link-hover cursor-pointer`}
                    onClick={() => {
                      setIsAdvancedCollapsed((x) => !x);
                    }}
                  >
                    <CaretRightOutlined rotate={isAdvancedCollapsed ? 0 : 90} className={`self-center`} />
                    <span>Advanced</span>
                    <div className="-ml-2">
                      <CpErrorWarningArrayIcon
                        names={[
                          `linode.authorizedKeys`,
                          `linode.preInstallScript`,
                          `linode.networking`,
                          `linode.autoscaler`,
                        ]}
                      />
                    </div>
                  </div>
                </div>
              </CardTitle>
            </div>
            <CardCollapsibleContent collapsed={isAdvancedCollapsed}>
              <div style={{ maxWidth: 600 }}>
                <CpArrayString name={`linode.authorizedKeys`} label={`Authorized Keys`} />
              </div>
              <ProviderPreInstallScript provider="linode" />
              <ProviderNetworking provider="linode" />
              <ProviderAutoscaler provider="linode" />
            </CardCollapsibleContent>
          </CardContent>
        </Card>
      </div>
      {browserKey === "regions" ? (
        <NGProviderBrowser
          title="Browse Linode Regions"
          initialValue={region}
          fetchData={async () => {
            const form = store.getState().form.instances[id];
            const values: Mk8sInput = form.values as any;

            const { data } = await request({
              service: "api",
              url: homeLink("mk8s") + "/-discover",
              method: "post",
              body: {
                objectType: "regions",
                cluster: inputToMk8s(values),
              },
            });
            return data.data;
          }}
          onOk={(value) => {
            formSetValue(id, `linode.region`, value as string);
            formTriggerValidation(id);
          }}
          onClose={() => setBrowserKey("")}
          labels={["Name", "Description"]}
          getData={(item: any) => [item.id, item.id, item.label]}
        />
      ) : null}
      {browserKey === "firewalls" ? (
        <NGProviderBrowser
          title="Browse Linode Firewalls"
          initialValue={firewallId}
          fetchData={async () => {
            const form = store.getState().form.instances[id];
            const values: Mk8sInput = form.values as any;

            const { data } = await request({
              service: "api",
              url: homeLink("mk8s") + "/-discover",
              method: "post",
              body: {
                objectType: "firewalls",
                cluster: inputToMk8s(values),
              },
            });
            return data.data;
          }}
          onOk={(value) => {
            formSetValue(id, `linode.firewallId`, String(value as string));
            formTriggerValidation(id);
          }}
          onClose={() => setBrowserKey("")}
          labels={["Name", "Description"]}
          getData={(item: any) => [item.id, item.id, item.label]}
        />
      ) : null}
      {browserKey === "images" ? (
        <NGProviderBrowser
          title="Browse Linode Images"
          initialValue={image}
          fetchData={async () => {
            const form = store.getState().form.instances[id];
            const values: Mk8sInput = form.values as any;

            const { data } = await request({
              service: "api",
              url: homeLink("mk8s") + "/-discover",
              method: "post",
              body: {
                objectType: "images",
                cluster: inputToMk8s(values),
              },
            });
            return data.data;
          }}
          onOk={(value) => {
            formSetValue(id, `linode.image`, value as string);
            formTriggerValidation(id);
          }}
          onClose={() => setBrowserKey("")}
          labels={["Name", "Description"]}
          getData={(item: any) => [item.id, item.id, item.label]}
        />
      ) : null}
      {browserKey === "vpcs" ? (
        <NGProviderBrowser
          title="Browse Linode VPCs"
          initialValue={vpcId}
          fetchData={async () => {
            const form = store.getState().form.instances[id];
            const values: Mk8sInput = form.values as any;

            const { data } = await request({
              service: "api",
              url: homeLink("mk8s") + "/-discover",
              method: "post",
              body: {
                objectType: "vpcs",
                cluster: inputToMk8s(values),
              },
            });
            return data.data;
          }}
          onOk={(value) => {
            formSetValue(id, `linode.vpcId`, String(value as string));
            formTriggerValidation(id);
          }}
          onClose={() => setBrowserKey("")}
          labels={["Name", "Description"]}
          getData={(item: any) => [item.id, item.id, item.label]}
        />
      ) : null}
      {browserKey === "users" ? (
        <NGProviderBrowser
          title="Browse Linode Users"
          initialValue={authorizedUsers.map((i) => i.value)}
          fetchData={async () => {
            const form = store.getState().form.instances[id];
            const values: Mk8sInput = form.values as any;

            const { data } = await request({
              service: "api",
              url: homeLink("mk8s") + "/-discover",
              method: "post",
              body: {
                objectType: "users",
                cluster: inputToMk8s(values),
              },
            });
            return data.data;
          }}
          multipleChoice
          onOk={(_value) => {
            const values: string[] = _value as any;
            const form = store.getState().form.instances[id];
            const formValues: Mk8sInput = form.values as any;

            const _authorizedUsers = cloneDeep(formValues.linode.authorizedUsers);
            for (const value of values) {
              if (_authorizedUsers.some((i) => i.value === value)) {
                continue;
              }
              _authorizedUsers.push({ id: uuidv4(), value: value });
            }
            formSetValue(id, `linode.authorizedUsers`, _authorizedUsers);
            formTriggerValidation(id);
          }}
          onClose={() => setBrowserKey("")}
          labels={["Username", "Email"]}
          getData={(item: any) => [item.username, item.username, item.email]}
        />
      ) : null}
    </>
  );
};
